/**
 * OneApplicationModule (OAM)
 * 
 * Displays detailed information about a single Forge Application, including:
 * - Application info (name, description, state)
 * - External links (frontend, backend, repo)
 * - Related UseCase and Cadrage info
 * - README content
 * - Recent logs
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppWindow, Boxes, CheckCircle2, Circle, ClipboardList, Clock, Copy, Download, Eye, EyeOff, FileText, GitBranch, Globe, Hammer, Loader2, Play, Plug, RefreshCw, ScrollText, Server, Square, Trash2, TriangleAlert, Unlink2, XCircle } from 'lucide-react';
import forgeService from '../../services/forgeService';
import {
  ForgeApplicationDetails,
  ForgeServiceItem,
  CustomAppVariablesResponse,
  ForgePod,
  ForgeLogEntry,
  ForgeLogsParams,
  Forge,
  ForgeClient,
} from '../../types';
import type { BuildInfo } from '../../types';
import { Badge } from '../Common/badge';
import MarkdownDisplay from '../Common/MarkdownDisplay';
import BuildLogsModal from './BuildLogsModal';
import notify from '@/lib/notify';

// Helper to capitalize first letter
const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

interface OneApplicationModuleProps {
  applicationId: string;
  useCaseId?: string | null;
  useCaseName?: string | null;
  cadrageId?: string | null;
  cadrageName?: string | null;
  showHeader?: boolean;  // Show header with icon and title (set to false when used in detail page)
  hideUseCaseLink?: boolean;  // Hide UseCase link (when shown in UseCase's tabs)
  hideCadrageLink?: boolean;  // Hide Cadrage link (when shown in Cadrage's tabs)
  showReadme?: boolean;
  className?: string;
  /** When set, shows the build progress card and auto-opens the logs modal */
  triggeredBuild?: BuildInfo | null;
  /** Called when the triggered build finishes (success or failure) */
  onBuildComplete?: () => void;
  /** Trigger a build (renders the Build action button when provided) */
  onBuild?: () => void;
  /** Trigger a delete (renders the Delete action button when provided) */
  onDelete?: () => void;
  /** Unlink the linked use case (and therefore the cadrage) from this app. Renders the Unlink button when provided and a cadrage is linked. */
  onUnlinkCadrage?: () => void;
  isBuilding?: boolean;
  isDeleting?: boolean;
  isUnlinking?: boolean;
  /** Extra action buttons rendered after the built-in ones in the top-right action row. */
  extraActions?: React.ReactNode;
}

// Build pipeline steps
const BUILD_STEPS = [
  { key: 'queued', labelKey: 'forge.application.stepQueued', label: 'Queued' },
  { key: 'cloning', labelKey: 'forge.application.stepCloning', label: 'Cloning' },
  { key: 'building', labelKey: 'forge.application.stepBuilding', label: 'Building' },
  { key: 'pushing', labelKey: 'forge.application.stepPushing', label: 'Pushing' },
  { key: 'deploying', labelKey: 'forge.application.stepDeploying', label: 'Deploying' },
] as const;

/** Map a build status string to the active step index (0-based) */
function getActiveStepIndex(status?: string | null): number {
  if (!status) return 0;
  switch (status.toLowerCase()) {
    case 'pending':
    case 'queued':
      return 0;
    case 'cloning':
      return 1;
    case 'building':
    case 'running':
      return 2;
    case 'pushing':
      return 3;
    case 'deploying':
      return 4;
    case 'success':
    case 'deployed':
      return BUILD_STEPS.length; // all done
    default:
      return 2; // default to "building" for unknown active statuses
  }
}

function isBuildActive(status?: string | null): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return ['pending', 'queued', 'cloning', 'building', 'running', 'pushing', 'deploying'].includes(s);
}

/** Build Progress Card shown above tabs while a build is in progress */
const BuildProgressCard: React.FC<{
  build: BuildInfo;
  status: string;
  onViewLogs: () => void;
}> = ({ build, status, onViewLogs }) => {
  const { t } = useTranslation();
  const activeIndex = getActiveStepIndex(status);
  const failed = status?.toLowerCase() === 'failed';
  const succeeded = status?.toLowerCase() === 'success';

  return (
    <div className={`p-4 rounded-lg border ${failed ? 'border-destructive/50 bg-destructive/5' : succeeded ? 'border-green-500/50 bg-green-500/5' : 'border-primary/50 bg-primary/5'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {!failed && !succeeded && <Loader2 className="h-5 w-5 text-primary animate-spin" />}
          {succeeded && <CheckCircle2 className="h-5 w-5 text-green-500" />}
          {failed && <XCircle className="h-5 w-5 text-destructive" />}
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {failed
                ? t('forge.application.buildFailed', 'Build Failed')
                : succeeded
                  ? t('forge.application.buildSucceeded', 'Build Succeeded')
                  : t('forge.application.buildInProgress', 'Build in Progress')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {build.branch && <span>{build.branch}</span>}
              {build.triggered_by && <span> · {build.triggered_by}</span>}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onViewLogs}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t('forge.application.viewLogs', 'View logs')}
        </button>
      </div>

      {/* Steps progress */}
      <div className="flex items-center gap-1">
        {BUILD_STEPS.map((step, idx) => {
          const isCompleted = idx < activeIndex;
          const isActive = idx === activeIndex && !failed && !succeeded;
          const isFailed = idx === activeIndex && failed;

          return (
            <React.Fragment key={step.key}>
              {idx > 0 && (
                <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-green-500' : isFailed ? 'bg-destructive' : 'bg-border'}`} />
              )}
              <div className="flex flex-col items-center gap-1">
                <div className={`flex items-center justify-center h-6 w-6 rounded-full ${
                  isCompleted ? 'bg-green-500 text-white' :
                  isActive ? 'bg-primary text-white' :
                  isFailed ? 'bg-destructive text-white' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {isCompleted && <CheckCircle2 className="h-4 w-4" />}
                  {isActive && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {isFailed && <XCircle className="h-4 w-4" />}
                  {!isCompleted && !isActive && !isFailed && <Circle className="h-3 w-3" />}
                </div>
                <span className={`text-[10px] font-medium whitespace-nowrap ${
                  isCompleted ? 'text-green-500' :
                  isActive ? 'text-primary' :
                  isFailed ? 'text-destructive' :
                  'text-muted-foreground'
                }`}>
                  {t(step.labelKey, step.label)}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {build.error_message && (
        <p className="text-sm text-destructive mt-3">{build.error_message}</p>
      )}
    </div>
  );
};

// Helper to get state badge variant based on status
const getStateBadgeVariant = (status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return 'default';
    case 'stopped':
      return 'secondary';
    case 'error':
    case 'build_failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

// Helper to get state icon based on status
const getStateIcon = (status?: string | null) => {
  if (!status) return null;
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return <Play className="h-4 w-4" />;
    case 'stopped':
      return <Square className="h-4 w-4" />;
    case 'error':
    case 'build_failed':
      return <TriangleAlert className="h-4 w-4" />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Install Services tab — list forge services that can be bound (installed)
// to the custom app. Once installed, show the env variables grouped by
// applicative service so the developer can wire them in their code.
// ---------------------------------------------------------------------------

// Default applicative service to inject into. Forge currently expects
// "backend" for the vast majority of bindings; we keep it implicit and let
// the user re-bind via the API directly if a frontend-only service is needed.
const DEFAULT_TARGET_SERVICE = 'backend';

const getServiceStatusBadgeVariant = (
  status?: string,
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'running':
    case 'ready':
    case 'deployed':
    case 'active':
      return 'default';
    case 'creating':
    case 'pending':
    case 'updating':
      return 'secondary';
    case 'error':
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

// Build the flat set of env var names currently set on the app, so we can
// check membership when deciding which forge services are "installed".
const collectAppVarNames = (vars: CustomAppVariablesResponse | null): Set<string> => {
  const names = new Set<string>();
  if (!vars) return names;
  for (const appSvc of vars.services) {
    for (const v of appSvc.variables) {
      names.add(v.name);
    }
  }
  return names;
};

// A service is considered "installed" when at least one of the variables it
// exposes (revealed via the /reveal endpoint) is already present in the app's
// env. Falls back to false if the service hasn't been revealed yet (loading
// or reveal failed).
const isServiceInstalled = (
  serviceVarNames: string[] | undefined,
  appVarNames: Set<string>,
): boolean => {
  if (!serviceVarNames || serviceVarNames.length === 0) return false;
  return serviceVarNames.some((name) => appVarNames.has(name));
};

// Shared hook used by both the Install Services and the Variables tab.
// Fetches forge services, app env variables and reveals each service's
// secrets in parallel. Returns refetch helpers for both the full payload
// (used after install) and just the app variables.
function useForgeServicesData(clientId: string, forgeId: string, appId: string) {
  const { t } = useTranslation();
  const [services, setServices] = useState<ForgeServiceItem[]>([]);
  const [variables, setVariables] = useState<CustomAppVariablesResponse | null>(null);
  const [serviceSecretsById, setServiceSecretsById] = useState<
    Record<string, { name: string; value: string }[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [servicesRes, varsRes] = await Promise.allSettled([
      forgeService.listForgeServices(clientId, forgeId),
      forgeService.getCustomAppVariables(clientId, forgeId, appId),
    ]);
    const fetchedServices: ForgeServiceItem[] =
      servicesRes.status === 'fulfilled' ? servicesRes.value : [];
    setServices(fetchedServices);
    setVariables(varsRes.status === 'fulfilled' ? varsRes.value : null);
    if (servicesRes.status === 'rejected' && varsRes.status === 'rejected') {
      setError(t('forge.application.installServices.errorLoading', 'Failed to load forge services'));
    }

    if (fetchedServices.length > 0) {
      const reveals = await Promise.allSettled(
        fetchedServices.map((svc) =>
          forgeService.revealForgeService(clientId, forgeId, svc.service_id),
        ),
      );
      const next: Record<string, { name: string; value: string }[]> = {};
      reveals.forEach((res, idx) => {
        const svc = fetchedServices[idx];
        next[svc.service_id] =
          res.status === 'fulfilled' && res.value.secret_values
            ? Object.entries(res.value.secret_values).map(([name, value]) => ({ name, value }))
            : [];
      });
      setServiceSecretsById(next);
    } else {
      setServiceSecretsById({});
    }
    setLoading(false);
  }, [clientId, forgeId, appId, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const refetchVariables = useCallback(async () => {
    try {
      const fresh = await forgeService.getCustomAppVariables(clientId, forgeId, appId);
      setVariables(fresh);
    } catch {
      // ignored — UI will keep showing previous data
    }
  }, [clientId, forgeId, appId]);

  return { services, variables, serviceSecretsById, loading, error, fetchAll, refetchVariables };
}

interface InstallServicesTabContentProps {
  clientId: string;
  forgeId: string;
  appId: string;
}

const InstallServicesTabContent: React.FC<InstallServicesTabContentProps> = ({
  clientId,
  forgeId,
  appId,
}) => {
  const { t } = useTranslation();
  const {
    services,
    variables,
    serviceSecretsById,
    loading,
    error,
    fetchAll,
    refetchVariables,
  } = useForgeServicesData(clientId, forgeId, appId);
  const [installingId, setInstallingId] = useState<string | null>(null);

  const handleInstall = async (svc: ForgeServiceItem) => {
    setInstallingId(svc.service_id);
    try {
      const response = await forgeService.createCustomAppServiceBinding(
        clientId,
        forgeId,
        appId,
        { service_id: svc.service_id, target_service: DEFAULT_TARGET_SERVICE },
      );
      notify.success(
        t('forge.application.installServices.installSuccess', {
          defaultValue: '{{count}} variables injected',
          count: response.injected_variables.length,
        }),
      );
      // Refresh the variables to reflect the binding
      await refetchVariables();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      notify.error(
        t('forge.application.installServices.installError', {
          defaultValue: 'Failed to install service: {{message}}',
          message,
        }),
      );
    } finally {
      setInstallingId(null);
    }
  };

  const appVarNames = collectAppVarNames(variables);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10 flex items-center justify-between gap-4">
        <p className="text-destructive">{error}</p>
        <button
          onClick={fetchAll}
          className="px-3 py-1.5 text-sm rounded-md border border-destructive/30 hover:bg-destructive/10"
        >
          {t('global.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Available services to install */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Plug className="h-4 w-4 text-primary" />
            {t('forge.application.installServices.availableTitle', 'Available services in this forge')}
          </h3>
          <button
            type="button"
            onClick={fetchAll}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t('global.refresh', 'Refresh')}
          </button>
        </div>
        {services.length === 0 ? (
          <div className="p-6 text-center border border-border rounded-lg bg-muted/30">
            <Boxes className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('forge.application.installServices.noServices', 'No services deployed in this forge')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {services.map((svc) => {
              const isInstalling = installingId === svc.service_id;
              const secrets = serviceSecretsById[svc.service_id] || [];
              const installed = isServiceInstalled(
                secrets.map((s) => s.name),
                appVarNames,
              );
              return (
                <div
                  key={svc.service_id}
                  className="border border-border rounded-lg bg-card p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Boxes className="h-4 w-4 text-primary shrink-0" />
                        <h4 className="text-sm font-semibold text-foreground truncate" title={svc.display_name}>
                          {svc.display_name || svc.service_name}
                        </h4>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {svc.service_type} · {svc.template_name}
                      </p>
                    </div>
                    <Badge variant={getServiceStatusBadgeVariant(svc.status)}>{svc.status}</Badge>
                  </div>

                  {installed ? (
                    <div className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-4 w-4" />
                      {t('forge.application.installServices.installed', 'Installed')}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleInstall(svc)}
                      disabled={isInstalling}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {isInstalling ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('forge.application.installServices.installing', 'Installing...')}
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          {t('forge.application.installServices.install', 'Install')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

// ---------------------------------------------------------------------------
// App Variables tab — show the env variables actually deployed on the app,
// grouped by applicative service. Values from the GitOps manifest are often
// empty (the deployment uses `valueFrom: secretKeyRef`), so we fall back to
// the actual secret value resolved via /reveal on the matching forge service.
// ---------------------------------------------------------------------------

interface AppVariablesTabContentProps {
  clientId: string;
  forgeId: string;
  appId: string;
}

const AppVariablesTabContent: React.FC<AppVariablesTabContentProps> = ({
  clientId,
  forgeId,
  appId,
}) => {
  const { t } = useTranslation();
  const { variables, serviceSecretsById, loading, error, fetchAll } =
    useForgeServicesData(clientId, forgeId, appId);
  const [revealedValues, setRevealedValues] = useState<Record<string, boolean>>({});
  const [revealAll, setRevealAll] = useState(false);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify.success(t('global.copied', 'Copied'));
    } catch {
      notify.error(t('global.copyFailed', 'Failed to copy'));
    }
  };

  const toggleReveal = (key: string) => {
    setRevealedValues((prev) => {
      const currentlyRevealed = key in prev ? prev[key] : revealAll;
      return { ...prev, [key]: !currentlyRevealed };
    });
  };

  const toggleRevealAll = () => {
    setRevealAll((prev) => {
      // Reset per-row overrides so the global state takes effect cleanly.
      setRevealedValues({});
      return !prev;
    });
  };

  // Build a flat name -> value map from all revealed services. Used as a
  // fallback when the app's manifest stored an empty value (secretKeyRef).
  const revealedByName: Record<string, string> = {};
  for (const list of Object.values(serviceSecretsById)) {
    for (const { name, value } of list) {
      if (value) revealedByName[name] = value;
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/10 flex items-center justify-between gap-4">
        <p className="text-destructive">{error}</p>
        <button
          onClick={fetchAll}
          className="px-3 py-1.5 text-sm rounded-md border border-destructive/30 hover:bg-destructive/10"
        >
          {t('global.retry', 'Retry')}
        </button>
      </div>
    );
  }

  const hasVariables =
    variables && variables.services.some((s) => s.variables.length > 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          {t('forge.application.appVariables.title', 'Application environment variables')}
        </h3>
        <div className="flex items-center gap-3">
          {hasVariables && (
            <button
              type="button"
              onClick={toggleRevealAll}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              {revealAll ? (
                <>
                  <EyeOff className="h-3.5 w-3.5" />
                  {t('forge.application.appVariables.hideAll', 'Hide all')}
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" />
                  {t('forge.application.appVariables.revealAll', 'Reveal all')}
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={fetchAll}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t('global.refresh', 'Refresh')}
          </button>
        </div>
      </div>
      {!hasVariables ? (
        <div className="p-6 text-center border border-border rounded-lg bg-muted/30">
          <p className="text-sm text-muted-foreground">
            {t(
              'forge.application.appVariables.empty',
              'No environment variables configured for this application yet.',
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {variables!.services
            .filter((s) => s.variables.length > 0)
            .map((appSvc) => (
              <div
                key={appSvc.service}
                className="border border-border rounded-lg bg-card overflow-hidden"
              >
                <div className="px-4 py-2 bg-muted/40 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{appSvc.service}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {appSvc.variables.length}{' '}
                      {t('forge.application.appVariables.variables', 'variables')}
                    </span>
                  </div>
                  <span
                    className="text-[11px] text-muted-foreground font-mono truncate"
                    title={appSvc.deployment_manifest_path}
                  >
                    {appSvc.deployment_manifest_path}
                  </span>
                </div>
                <div className="divide-y divide-border">
                  {appSvc.variables.map((v) => {
                    const key = `${appSvc.service}:${v.name}`;
                    // Per-row toggle wins over the global "reveal all" state so the
                    // user can keep one secret visible after hiding the rest.
                    const revealed = key in revealedValues ? !!revealedValues[key] : revealAll;
                    const effectiveValue = v.value || revealedByName[v.name] || '';
                    const fromSecret = !v.value && !!revealedByName[v.name];
                    return (
                      <div
                        key={key}
                        className="px-4 py-2 grid grid-cols-[1fr,2fr,auto] gap-3 items-center text-sm"
                      >
                        <code className="text-foreground font-mono text-xs break-all">
                          {v.name}
                        </code>
                        <code className="text-muted-foreground font-mono text-xs break-all">
                          {effectiveValue
                            ? revealed
                              ? effectiveValue
                              : '••••••••'
                            : t('forge.application.appVariables.emptyValue', '(empty)')}
                          {fromSecret && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                              {t('forge.application.appVariables.fromSecret', 'secret')}
                            </span>
                          )}
                        </code>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleReveal(key)}
                            disabled={!effectiveValue}
                            className="p-1.5 rounded hover:bg-accent text-muted-foreground disabled:opacity-30"
                            title={revealed ? t('global.hide', 'Hide') : t('global.show', 'Show')}
                          >
                            {revealed ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopy(effectiveValue)}
                            disabled={!effectiveValue}
                            className="p-1.5 rounded hover:bg-accent text-muted-foreground disabled:opacity-30"
                            title={t('global.copy', 'Copy')}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// App Logs tab — fetches pods for this specific custom app via the dedicated
// backend endpoint (which applies k8s_safe_name truncation server-side) and
// shows live container logs for the selected pod via the forge logs endpoint.
// ---------------------------------------------------------------------------

interface AppLogsTabContentProps {
  clientId: string;
  forgeId: string;
  appId: string;
}

const AppLogsTabContent: React.FC<AppLogsTabContentProps> = ({ clientId, forgeId, appId }) => {
  const { t } = useTranslation();
  const [allPods, setAllPods] = useState<ForgePod[]>([]);
  const [selectedPod, setSelectedPod] = useState<string>('');
  const [tailLines, setTailLines] = useState<number>(200);
  const [logs, setLogs] = useState<ForgeLogEntry[]>([]);
  const [podsLoading, setPodsLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [podsError, setPodsError] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);

  const fetchPods = useCallback(async () => {
    setPodsLoading(true);
    setPodsError(null);
    try {
      const data = await forgeService.getAppPods(clientId, forgeId, appId);
      setAllPods(data);
      setSelectedPod((prev) => {
        if (prev && data.some((p) => p.name === prev)) return prev;
        return data[0]?.name ?? '';
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setPodsError(e.response?.data?.detail || e.message || 'Failed to load pods');
      setAllPods([]);
    } finally {
      setPodsLoading(false);
    }
  }, [clientId, forgeId, appId]);

  const fetchLogs = useCallback(async () => {
    if (!selectedPod) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const params: ForgeLogsParams = { pod: selectedPod, tail_lines: tailLines };
      const res = await forgeService.getForgeLogs(clientId, forgeId, params);
      setLogs(res.logs ?? []);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setLogsError(e.response?.data?.detail || e.message || 'Failed to load logs');
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [clientId, forgeId, selectedPod, tailLines]);

  useEffect(() => {
    fetchPods();
  }, [fetchPods]);

  useEffect(() => {
    if (selectedPod) fetchLogs();
  }, [fetchLogs, selectedPod]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-48">
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            {t('forge.application.appLogs.filterPod', 'Pod')}
          </label>
          <select
            value={selectedPod}
            onChange={(e) => setSelectedPod(e.target.value)}
            disabled={podsLoading || allPods.length === 0}
            className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          >
            {allPods.length === 0 ? (
              <option value="">
                {podsLoading
                  ? t('forge.application.appLogs.loadingPods', 'Loading pods…')
                  : t('forge.application.appLogs.noPods', 'No pods found for this app')}
              </option>
            ) : (
              allPods.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name} ({p.phase})
                </option>
              ))
            )}
          </select>
        </div>
        <div className="w-32">
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            {t('forge.logs.tailLines', 'Lines')}
          </label>
          <select
            value={tailLines}
            onChange={(e) => setTailLines(Number(e.target.value))}
            className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => {
            fetchPods();
            if (selectedPod) fetchLogs();
          }}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-border hover:bg-accent"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('global.refresh', 'Refresh')}
        </button>
      </div>

      {podsError && <p className="text-sm text-destructive">{podsError}</p>}

      <div className="border border-border rounded-lg bg-gray-900 text-gray-100 overflow-hidden">
        <div className="max-h-[600px] overflow-auto">
          {logsLoading ? (
            <div className="text-center py-8 text-gray-400">
              {t('forge.logs.loading', 'Loading logs...')}
            </div>
          ) : logsError ? (
            <div className="text-center py-8 text-red-400 px-4">{logsError}</div>
          ) : !selectedPod ? (
            <div className="text-center py-8 text-gray-400">
              {t('forge.application.appLogs.selectPod', 'Select a pod to view its logs')}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {t('forge.logs.noLogs', 'No logs available')}
            </div>
          ) : (
            <pre className="p-4 font-mono text-xs whitespace-pre-wrap break-words">
              {logs
                .map((e) =>
                  `${e.timestamp ? `[${e.timestamp}] ` : ''}${e.level ? `${e.level} ` : ''}${e.message}`,
                )
                .join('\n')}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export const OneApplicationModule: React.FC<OneApplicationModuleProps> = ({
  applicationId,
  useCaseId,
  useCaseName,
  cadrageId,
  cadrageName,
  showHeader = true,
  hideUseCaseLink = false,
  hideCadrageLink = false,
  showReadme = true,
  className = '',
  triggeredBuild = null,
  onBuildComplete,
  onBuild,
  onDelete,
  onUnlinkCadrage,
  isBuilding = false,
  isDeleting = false,
  isUnlinking = false,
  extraActions,
}) => {
  const { t } = useTranslation();
  const [application, setApplication] = useState<ForgeApplicationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'builds' | 'logs' | 'metadata' | 'services' | 'variables' | 'readme'>('overview');
  const [buildLogsBuild, setBuildLogsBuild] = useState<BuildInfo | null>(null);
  const [activeBuild, setActiveBuild] = useState<BuildInfo | null>(null);
  const [activeBuildStatus, setActiveBuildStatus] = useState<string>('');
  const [showBuildConfirm, setShowBuildConfirm] = useState(false);
  const [parentForge, setParentForge] = useState<Forge | null>(null);
  const [parentClient, setParentClient] = useState<ForgeClient | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevTriggeredBuildRef = useRef<string | null>(null);

  const fetchApplication = useCallback(async () => {
    try {
      const data = await forgeService.getApplicationDetails(applicationId);
      setApplication(data);
      return data;
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status === 404) {
          setError(t('forge.application.notFound', 'Application non trouvée (peut-être supprimée)'));
        } else {
          const errorMessage = err instanceof Error ? err.message : t('forge.application.errorLoading', 'Failed to load application details');
          setError(errorMessage);
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : t('forge.application.errorLoading', 'Failed to load application details');
        setError(errorMessage);
      }
      console.error('Error fetching application:', err);
      return null;
    }
  }, [applicationId, t]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      await fetchApplication();
      setIsLoading(false);
    };
    load();
  }, [fetchApplication]);

  // Fetch parent forge & client (used by overview link and metadata names).
  // Failures are silent — we just fall back to IDs.
  useEffect(() => {
    if (!application) return;
    let cancelled = false;
    (async () => {
      const [forgeRes, clientRes] = await Promise.allSettled([
        forgeService.getForge(application.forge_id),
        forgeService.getClient(application.client_id),
      ]);
      if (cancelled) return;
      setParentForge(forgeRes.status === 'fulfilled' ? forgeRes.value : null);
      setParentClient(clientRes.status === 'fulfilled' ? clientRes.value : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [application?.forge_id, application?.client_id]);

  // When a build is triggered from parent, auto-open modal and start polling
  useEffect(() => {
    if (!triggeredBuild) return;
    const buildId = triggeredBuild.build_id;
    if (buildId === prevTriggeredBuildRef.current) return;
    prevTriggeredBuildRef.current = buildId;

    setActiveBuild(triggeredBuild);
    setActiveBuildStatus(triggeredBuild.status || 'pending');
    setBuildLogsBuild(triggeredBuild);
  }, [triggeredBuild]);

  // Polling while build is active
  useEffect(() => {
    if (!activeBuild || !isBuildActive(activeBuildStatus)) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Poll every 8 seconds
    pollingRef.current = setInterval(async () => {
      const data = await fetchApplication();
      if (data?.current_build) {
        const newStatus = data.current_build.status;
        setActiveBuildStatus(newStatus);
        setActiveBuild(data.current_build);

        if (!isBuildActive(newStatus)) {
          // Build finished
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          onBuildComplete?.();
          // Auto-hide progress card after 10s on success
          if (newStatus === 'success') {
            setTimeout(() => setActiveBuild(null), 10000);
          }
        }
      }
    }, 8000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeBuild, activeBuildStatus, fetchApplication, onBuildComplete]);

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-muted rounded w-2/3 mb-6"></div>
          <div className="h-32 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 border border-destructive/50 rounded-lg bg-destructive/10 ${className}`}>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!application) {
    return (
      <div className={`p-6 text-center border border-border rounded-lg bg-muted/30 ${className}`}>
        <AppWindow className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">{t('forge.application.notFound', 'Application not found')}</p>
      </div>
    );
  }

  const frontendUrl = application.urls?.frontend || application.urls?.frontend_url || application.deployment_info?.frontend_url;
  const backendUrl = application.urls?.backend || application.urls?.backend_url || application.deployment_info?.backend_url;
  const sourceRepoUrl = application.config?.source_repo_url || application.source_repo_url;

  const iconBtnBase =
    'inline-flex items-center justify-center h-9 w-9 rounded-md transition-colors disabled:opacity-50';

  const actionButtons = (
    <div className="flex items-center gap-1.5 flex-wrap">
      {frontendUrl && (
        <a
          href={frontendUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${iconBtnBase} border border-border bg-card hover:bg-accent`}
          title={t('forge.application.frontend', 'Frontend')}
          aria-label={t('forge.application.frontend', 'Frontend')}
        >
          <Globe className="h-4 w-4" />
        </a>
      )}
      {backendUrl && (
        <a
          href={backendUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${iconBtnBase} border border-border bg-card hover:bg-accent`}
          title={t('forge.application.backend', 'Backend')}
          aria-label={t('forge.application.backend', 'Backend')}
        >
          <Server className="h-4 w-4" />
        </a>
      )}
      {sourceRepoUrl && (
        <a
          href={sourceRepoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${iconBtnBase} border border-border bg-card hover:bg-accent`}
          title={sourceRepoUrl}
          aria-label={t('forge.application.sourceRepoUrl', 'Source repo URL')}
        >
          <GitBranch className="h-4 w-4" />
        </a>
      )}
      {onBuild && (
        <button
          type="button"
          onClick={() => setShowBuildConfirm(true)}
          disabled={isBuilding || isDeleting}
          className={`${iconBtnBase} bg-primary text-primary-foreground hover:bg-primary/90`}
          title={isBuilding ? t('forge.application.building', 'Building...') : t('forge.application.build', 'Build')}
          aria-label={t('forge.application.build', 'Build')}
        >
          {isBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hammer className="h-4 w-4" />}
        </button>
      )}
      {onUnlinkCadrage && cadrageId && !hideCadrageLink && (
        <button
          type="button"
          onClick={onUnlinkCadrage}
          disabled={isBuilding || isDeleting || isUnlinking}
          className={`${iconBtnBase} border border-border bg-card hover:bg-accent`}
          title={t('forge.application.unlinkCadrageTooltip', 'Detach this app from the linked solution / use case')}
          aria-label={t('forge.application.unlinkCadrage', 'Unlink')}
        >
          {isUnlinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink2 className="h-4 w-4" />}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={isBuilding || isDeleting}
          className={`${iconBtnBase} bg-destructive text-destructive-foreground hover:bg-destructive/90`}
          title={t('forge.application.delete', 'Delete')}
          aria-label={t('forge.application.delete', 'Delete')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {extraActions}
    </div>
  );

  const tabButtonClass = (isActive: boolean) =>
    `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
      isActive
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header - title on left, action buttons on right */}
      {showHeader ? (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="p-3 bg-primary/10 rounded-lg shrink-0">
              <AppWindow className="h-8 w-8 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 min-w-0">
                <h2
                  className="text-2xl font-bold text-foreground truncate min-w-0"
                  title={capitalizeFirst(application.name)}
                >
                  {capitalizeFirst(application.name)}
                </h2>
                {application.status && (
                  <Badge variant={getStateBadgeVariant(application.status)} className="flex items-center gap-1 shrink-0">
                    {getStateIcon(application.status)}
                    {application.status}
                  </Badge>
                )}
                {application.app_type && (
                  <Badge variant="outline" className="shrink-0">{application.app_type}</Badge>
                )}
              </div>
              {application.template_name && (
                <p className="text-muted-foreground mt-2 truncate" title={application.template_name}>
                  {t('forge.application.templateLabel')} {application.template_name}
                </p>
              )}
              {application.health_status && (
                <p className="text-sm text-muted-foreground mt-1">
                  {t('forge.application.healthStatus', 'Health')}: {application.health_status}
                </p>
              )}
            </div>
          </div>
          <div className="shrink-0">{actionButtons}</div>
        </div>
      ) : (
        <div className="flex justify-end">{actionButtons}</div>
      )}

      {/* Build confirmation dialog */}
      {showBuildConfirm && onBuild && (
        <div className="p-4 border border-primary/40 bg-primary/5 rounded-lg">
          <div className="flex items-start gap-3">
            <Hammer className="h-6 w-6 text-primary flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">
                {t('forge.application.buildConfirmTitle', 'Trigger a new build?')}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'forge.application.buildConfirmMessage',
                  'A new build will be queued for this application on the main branch. The current deployment will be replaced once the build succeeds.',
                )}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBuildConfirm(false);
                    onBuild();
                  }}
                  disabled={isBuilding}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Hammer className="h-4 w-4" />
                  {t('forge.application.buildConfirmYes', 'Yes, build now')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBuildConfirm(false)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-border hover:bg-accent"
                >
                  {t('global.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last build info */}
      {application.last_build_at && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          {t('forge.application.lastBuild', 'Last build')}:{' '}
          {new Date(application.last_build_at).toLocaleString()}
        </div>
      )}

      {/* Build Progress Card - shown while a build is active */}
      {activeBuild && (
        <BuildProgressCard
          build={activeBuild}
          status={activeBuildStatus}
          onViewLogs={() => setBuildLogsBuild(activeBuild)}
        />
      )}

      {/* Tabs */}
      <div>
        <div className="flex border-b border-border flex-wrap">
          <button onClick={() => setActiveTab('overview')} className={tabButtonClass(activeTab === 'overview')}>
            {t('forge.application.overview', 'Overview')}
          </button>
          <button onClick={() => setActiveTab('builds')} className={tabButtonClass(activeTab === 'builds')}>
            <Hammer className="h-4 w-4" />
            {t('forge.application.buildsTab', 'Builds')}
          </button>
          <button onClick={() => setActiveTab('logs')} className={tabButtonClass(activeTab === 'logs')}>
            <ScrollText className="h-4 w-4" />
            {t('forge.application.logsTab', 'Logs')}
          </button>
          <button onClick={() => setActiveTab('metadata')} className={tabButtonClass(activeTab === 'metadata')}>
            {t('forge.application.metadata', 'Metadata')}
          </button>
          <button onClick={() => setActiveTab('services')} className={tabButtonClass(activeTab === 'services')}>
            <Plug className="h-4 w-4" />
            {t('forge.application.installServices.tab', 'Install Services')}
          </button>
          <button onClick={() => setActiveTab('variables')} className={tabButtonClass(activeTab === 'variables')}>
            <FileText className="h-4 w-4" />
            {t('forge.application.appVariables.tab', 'Variables')}
          </button>
          {showReadme && (
            <button onClick={() => setActiveTab('readme')} className={tabButtonClass(activeTab === 'readme')}>
              <FileText className="h-4 w-4" />
              {t('forge.application.readme')}
            </button>
          )}
        </div>

          <div className="pt-4">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Full application name */}
                <div className="p-4 border border-border rounded-lg bg-card">
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.name', 'Name')}</p>
                  <p className="text-foreground font-medium break-all">{capitalizeFirst(application.name)}</p>
                </div>

                {/* Parent Forge link */}
                <div className="p-4 border border-border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Server className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('forge.application.parentForge', 'Parent Forge')}
                        </p>
                        <p className="text-foreground font-medium mt-0.5">
                          {parentForge
                            ? capitalizeFirst(parentForge.display_name || parentForge.name)
                            : t('global.loading', 'Loading...')}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/forges/${application.forge_id}`}
                      className="flex items-center gap-1.5 text-primary hover:underline text-sm"
                    >
                      <Server className="h-4 w-4" />
                      {t('global.view', 'View')}
                    </Link>
                  </div>
                </div>

                {/* Linked Use Case + Cadrage */}
                {((!hideUseCaseLink && useCaseId) || (!hideCadrageLink && cadrageId)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {!hideUseCaseLink && useCaseId && (
                      <div className="p-4 border border-border rounded-lg bg-muted/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <ClipboardList className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">
                                {t('forge.application.linkedUseCase', 'Linked Use Case')}
                              </p>
                              <p className="text-foreground font-medium mt-0.5">
                                {useCaseName || t('forge.application.useCase', 'Use Case')}
                              </p>
                            </div>
                          </div>
                          <Link
                            to={`/cas-usage/${useCaseId}`}
                            className="flex items-center gap-1.5 text-primary hover:underline text-sm"
                          >
                            <ClipboardList className="h-4 w-4" />
                            {t('global.view', 'View')}
                          </Link>
                        </div>
                      </div>
                    )}

                    {!hideCadrageLink && cadrageId && (
                      <div className="p-4 border border-border rounded-lg bg-muted/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <FileText className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-muted-foreground">
                                {t('forge.application.linkedCadrage', 'Linked Solution')}
                              </p>
                              <p className="text-foreground font-medium mt-0.5">
                                {cadrageName || t('forge.application.cadrage', 'Solution')}
                              </p>
                            </div>
                          </div>
                          <Link
                            to={`/cadrages/${cadrageId}`}
                            className="flex items-center gap-1.5 text-primary hover:underline text-sm"
                          >
                            <FileText className="h-4 w-4" />
                            {t('global.view', 'View')}
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Configuration Section */}
                {application.config && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      {t('forge.application.configuration', 'Configuration')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border border-border rounded-lg bg-card">
                        <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.createdBy')}</p>
                        <p className="text-foreground">{application.config.created_by || application.created_by || t('global.notAvailable')}</p>
                      </div>
                      <div className="p-4 border border-border rounded-lg bg-card">
                        <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.appType')}</p>
                        <p className="text-foreground">{application.config.app_type || application.app_type || t('global.notAvailable')}</p>
                      </div>
                    </div>

                    {/* Resource Limits */}
                    {application.config.resource_limits && Object.keys(application.config.resource_limits).length > 0 && (
                      <div className="p-4 border border-border rounded-lg bg-card">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('forge.application.resourceLimits')}</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(application.config.resource_limits).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{key}:</span>
                              <span className="text-sm text-foreground font-mono">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Build Config */}
                    {application.config.build_config && Object.keys(application.config.build_config).length > 0 && (
                      <div className="p-4 border border-border rounded-lg bg-card">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('forge.application.buildConfiguration')}</p>
                        <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">
                          {JSON.stringify(application.config.build_config, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Runtime Config */}
                    {application.config.runtime_config && Object.keys(application.config.runtime_config).length > 0 && (
                      <div className="p-4 border border-border rounded-lg bg-card">
                        <p className="text-sm font-medium text-muted-foreground mb-2">{t('forge.application.runtimeConfiguration')}</p>
                        <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40">
                          {JSON.stringify(application.config.runtime_config, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}

            {activeTab === 'builds' && (
              <div className="space-y-6">
                {/* Current Build */}
                {application.current_build && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('forge.application.currentBuild', 'Current Build')}
                    </h3>
                    <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.status')}</p>
                          <Badge variant={application.current_build.status === 'success' ? 'default' : application.current_build.status === 'failed' ? 'destructive' : 'secondary'}>
                            {application.current_build.status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.branch')}</p>
                          <p className="text-sm text-foreground font-mono">{application.current_build.branch || t('global.notAvailable')}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.triggeredBy')}</p>
                          <p className="text-sm text-foreground">{application.current_build.triggered_by || t('global.notAvailable')}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.duration')}</p>
                          <p className="text-sm text-foreground">{application.current_build.duration_seconds ? `${application.current_build.duration_seconds}s` : t('global.notAvailable')}</p>
                        </div>
                      </div>
                      {application.current_build.commit_sha && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.commitSha')}</p>
                          <p className="text-sm text-foreground font-mono">{application.current_build.commit_sha}</p>
                        </div>
                      )}
                      {(application.current_build.backend_image_tag || application.current_build.frontend_image_tag) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {application.current_build.backend_image_tag && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.backendImage')}</p>
                              <p className="text-sm text-foreground font-mono break-all">{application.current_build.backend_image_tag}</p>
                            </div>
                          )}
                          {application.current_build.frontend_image_tag && (
                            <div>
                              <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.frontendImage')}</p>
                              <p className="text-sm text-foreground font-mono break-all">{application.current_build.frontend_image_tag}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {application.current_build.error_message && (
                        <div className="p-3 bg-destructive/10 border border-destructive rounded">
                          <p className="text-sm font-medium text-destructive mb-1">{t('global.error')}</p>
                          <p className="text-sm text-foreground">{application.current_build.error_message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Deployment Information */}
                {application.deployment_info && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('forge.application.deploymentInfo', 'Deployment Information')}
                    </h3>
                    <div className="p-4 border border-border rounded-lg bg-card space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.syncStatus')}</p>
                          <Badge variant="outline">{application.deployment_info.sync_status || t('global.notDefined')}</Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.healthStatus')}</p>
                          <Badge variant={application.deployment_info.health_status === 'Healthy' ? 'default' : 'secondary'}>
                            {application.deployment_info.health_status || t('global.notDefined')}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.argocdApp')}</p>
                          <p className="text-sm text-foreground font-mono">{application.deployment_info.argocd_app_name || t('global.notAvailable')}</p>
                        </div>
                      </div>
                      {application.deployment_info.deployed_at && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.deployedAt')}</p>
                          <p className="text-sm text-foreground">{new Date(application.deployment_info.deployed_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Docker Images */}
                {application.docker_images && Object.keys(application.docker_images).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('forge.application.dockerImages', 'Docker Images')}
                    </h3>
                    <div className="p-4 border border-border rounded-lg bg-card">
                      <div className="space-y-2">
                        {Object.entries(application.docker_images).map(([key, value]) => (
                          <div key={key} className="flex items-start gap-2">
                            <span className="text-sm text-muted-foreground min-w-24">{key}:</span>
                            <code className="text-sm text-foreground break-all flex-1">{value}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resource Usage */}
                {application.resource_usage && Object.keys(application.resource_usage).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('forge.application.resourceUsage', 'Resource Usage')}
                    </h3>
                    <div className="p-4 border border-border rounded-lg bg-card">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(application.resource_usage).map(([key, value]) => (
                          <div key={key}>
                            <p className="text-sm font-medium text-muted-foreground mb-1">{key}</p>
                            <p className="text-sm text-foreground font-mono">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Build History */}
                {application.build_history && application.build_history.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('forge.application.buildHistory', 'Build History')}
                    </h3>
                    <div className="space-y-2">
                      {application.build_history.slice(0, 5).map((build, index) => (
                        <div key={build.build_id || index} className="p-4 border border-border rounded-lg bg-card">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Badge variant={build.status === 'success' ? 'default' : build.status === 'failed' ? 'destructive' : 'secondary'}>
                                {build.status}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{build.branch || 'main'}</span>
                              {build.commit_sha && (
                                <code className="text-xs text-muted-foreground">{build.commit_sha.substring(0, 7)}</code>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setBuildLogsBuild(build)}
                                className="text-sm font-medium text-primary hover:underline"
                              >
                                {t('forge.application.viewLogs', 'View logs')}
                              </button>
                              <span className="text-sm text-muted-foreground">
                                {build.started_at && new Date(build.started_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          {build.duration_seconds && (
                            <p className="text-sm text-muted-foreground">{t('forge.application.durationLabel')} {build.duration_seconds}s</p>
                          )}
                          {build.error_message && (
                            <p className="text-sm text-destructive mt-2">{build.error_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'logs' && (
              <AppLogsTabContent
                clientId={application.client_id}
                forgeId={application.forge_id}
                appId={application.app_id}
              />
            )}

            {activeTab === 'metadata' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border border-border rounded-lg bg-card">
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.applicationName', 'Application')}</p>
                  <p className="text-sm text-foreground break-all">{capitalizeFirst(application.name)}</p>
                </div>
                <div className="p-4 border border-border rounded-lg bg-card">
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.forgeName', 'Forge')}</p>
                  {parentForge ? (
                    <Link
                      to={`/forges/${application.forge_id}`}
                      className="text-sm text-primary hover:underline break-all"
                    >
                      {capitalizeFirst(parentForge.display_name || parentForge.name)}
                    </Link>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">{t('global.loading', 'Loading...')}</p>
                  )}
                </div>
                <div className="p-4 border border-border rounded-lg bg-card">
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.clientName', 'Client')}</p>
                  {parentClient ? (
                    <p className="text-sm text-foreground break-all">{capitalizeFirst(parentClient.name)}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">{t('global.loading', 'Loading...')}</p>
                  )}
                </div>
                {application.created_at && (
                  <div className="p-4 border border-border rounded-lg bg-card">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.createdAt')}</p>
                    <p className="text-sm text-foreground">{new Date(application.created_at).toLocaleString()}</p>
                  </div>
                )}
                {application.updated_at && (
                  <div className="p-4 border border-border rounded-lg bg-card">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.updatedAt')}</p>
                    <p className="text-sm text-foreground">{new Date(application.updated_at).toLocaleString()}</p>
                  </div>
                )}
                {application.last_health_check && (
                  <div className="p-4 border border-border rounded-lg bg-card">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{t('forge.application.lastHealthCheck')}</p>
                    <p className="text-sm text-foreground">{new Date(application.last_health_check).toLocaleString()}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'services' && (
              <InstallServicesTabContent
                clientId={application.client_id}
                forgeId={application.forge_id}
                appId={application.app_id}
              />
            )}

            {activeTab === 'variables' && (
              <AppVariablesTabContent
                clientId={application.client_id}
                forgeId={application.forge_id}
                appId={application.app_id}
              />
            )}

            {activeTab === 'readme' && showReadme && (
              <div className="p-4 border border-border rounded-lg bg-muted/30">
                {application.readme_content?.trim() ? (
                  <MarkdownDisplay
                    content={application.readme_content}
                    emptyMessage={t('forge.application.noReadme', 'No README available')}
                  />
                ) : application.source_repo_url ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="text-sm">{t('forge.application.sourceRepo')} <code>{application.source_repo_url}</code></p>
                    <p className="text-muted-foreground text-sm mt-4">
                      {t('forge.application.noReadme', 'No README available')}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    {t('forge.application.noReadme', 'No README available')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

      <BuildLogsModal
        open={!!buildLogsBuild}
        onClose={() => setBuildLogsBuild(null)}
        application={application}
        build={buildLogsBuild}
      />
    </div>
  );
};

export default OneApplicationModule;
